import { memo, useMemo } from 'react'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { device } from '@/common/constants/device'

import Text from '@/components/common/Text'
import BaseLink from '@/components/common/BaseLink'
import { useSelector } from 'react-redux'
import { PAYMENT_SOURCE } from '@/common/constants/cookies'
import Accordion from '@/components/layout/Footer/FooterAccordion'
import { getCookie } from 'cookies-next'
import { SEARCH_COOKIE_KEY } from '@/components/Ads/AdsHeader'
import { useTranslations } from 'next-intl'

const LANGUAGE_CODES = {
  es: 'es',
  pt: 'pt',
}

const TELEGRAM_LINK = 'https://t.me/adheart'
const TELEGRAM_LINK_EN = 'https://t.me/adheart_english'

const Layout = styled.footer`
  width: 100%;
  background: #fff;
  margin-top: 1.75rem;

  @media ${device.laptopL} {
    margin-top: 2.75rem;
  }
`

const Container = styled.div`
  max-width: 1660px;
  padding: 24px 16px;
  margin: 0 auto;

  @media ${device.laptopM} {
    padding: 24px 0 16px 0;

    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }
`

const LogoDataWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
`

const Logo = styled(BaseLink)`
  width: 104px;
  height: 44px;
  background-image: url('/_next_static/logo.png');
  background-size: contain;
  background-repeat: no-repeat;
`

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Divider = styled.div`
  margin: 8px 0 16px;
  width: 100%;
  height: 1px;
  background-color: #e0e1e2;

  @media ${device.laptopM} {
    margin: 24px 0 16px;
  }
`

const TextStyled = styled(Text)`
  font-size: 14px;
`

const ByUrl = styled.div`
  color: var(--grey);
  margin-left: 0.25em;
  cursor: pointer;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.21px;
`

const BottomBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  gap: 24px;

  @media ${device.tablet} {
    gap: 0;
    flex-direction: row;
    align-items: center;
    align-items: center;
    justify-content: space-between;
  }
`

const LinkTitle = styled(Text)`
  color: #3f3f49;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.225px;
`

const RulsLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: #6e7278;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.225px;
`

const BaseLinkWrap = styled(BaseLink)`
  line-height: 24px;
  letter-spacing: -0.015em;
  font-weight: 400;
  color: var(--grey);
  width: fit-content;
`

const AgreemntText = styled(Text)`
  color: #6e7278;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.21px;
`

const LinkWrapperMobile = styled.li`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: dense;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;

  @media ${device.tablet} {
    display: none;
  }
`

const LinkWrapperDesktop = styled.li`
  display: none;

  @media ${device.tablet} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
  }

  @media ${device.laptopM} {
    gap: 40px;
  }
`

const SocialsList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`

const SocialsItem = styled.li`
  cursor: pointer;
`
const Block = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  @media ${device.laptopM} {
    justify-content: space-between;
    flex-direction: row;
    gap: 80px;
    align-items: flex-start;
  }
`

const SocialLink = styled.a`
  display: block;
  padding: 0.5rem;
`

const SocialImage = styled.img`
  width: 24px;
  height: 24px;
  background-size: cover;
  background-repeat: no-repeat;
  border-style: none;
`

const Footer = memo(() => {
  const date = new Date().getFullYear()
  const t = useTranslations('common')
  const router = useRouter()
  const { locale } = router
  const BASE_URL = process.env.BASE_URL

  const { user, subscription, referralBalance } = useSelector(
    (state) => state.user
  )

  const isUserAuth = useMemo(
    () => user && subscription && referralBalance,
    [user, subscription, referralBalance]
  )

  const searchCookie = getCookie(SEARCH_COOKIE_KEY)

  const FOOTER_LINKS = useMemo(() => {
    return [
      {
        title: 'ourProducts',
        links: [
          {
            id: 1,
            href: isUserAuth
              ? searchCookie === 'classic'
                ? '/teasers'
                : 'ads'
              : '/register',
            text: 'facebookSpyTool',
            needChangePath: searchCookie === 'classic',
          },
          {
            id: 2,
            href: isUserAuth
              ? searchCookie === 'classic'
                ? '/teasers'
                : 'ads'
              : '/register',
            text: 'instagramSpyTool',
            needChangePath: searchCookie === 'classic',
          },
          {
            id: 3,
            href: isUserAuth ? '/tools/utm-builder' : '/register',
            text: 'utmBuilder',
            needChangePath: false,
          },
          {
            id: 4,
            href: `/tools/uniq-creative`,
            text: 'creativeUniqueizer',
            needChangePath: false,
          },
          {
            id: 5,
            href: `/help/wiki`,
            text: 'knowledgeBase',
            needChangePath: false,
          },
        ],
      },
      {
        title: 'payment',
        links: [
          {
            id: 1,
            href: '/premium',
            text: 'pricing',
            needChangePath: false,
          },
          {
            id: 2,
            href: '/premium',
            text: 'teamPricing',
            needChangePath: false,
          },
          {
            id: 3,
            href: `/terms-of-service`,
            text: 'refundPolicy',
            needChangePath: false,
          },
        ],
      },
      {
        title: 'instructions',
        links: [
          {
            id: 1,
            text: 'adheartSearch',
            languages: {
              ru: '/blog/adheart-instruktsiia-po-primienieniiu',
              uk: '/blog/adheart-instruktsiia-po-primienieniiu',
            },
            needChangePath: true,
          },
          {
            id: 2,
            text: 'affiliateMarketingInstructions',
            languages: {
              uk: '/blog/arbitrazh-trafiku-u-2024-rotsi-shcho-kazhut-fakhivtsi',
            },
            needChangePath: true,
          },
          {
            id: 3,
            text: 'facebookAdsInfo',
            languages: {
              uk: '/help/wiki/formats-ua',
              pt: '/help/wiki/formats-pt',
            },
            needChangePath: false,
          },
          {
            id: 4,
            text: 'utmTagsInfo',
            languages: {
              uk: '/blog/vsie-pro-utm-mitki-iak-pravilno-analizuvati-trafik',
            },
            needChangePath: true,
          },
          {
            id: 5,
            text: 'improveAdsWithAdheart',
            languages: {
              uk: '/blog/8-krokiv-do-iefiektivnogho-analizu-rieklami-v-instagram-za-dopomoghoiu-siervis-adheart',
            },
            needChangePath: true,
          },
          {
            id: 6,
            text: 'facebookAdGoals',
            languages: {
              en: '/help/wiki/targets-en',
              es: '/help/wiki/targets-en',
              ru: '/help/wiki/fb-targets',
              uk: '/help/wiki/targets-ua',
              pt: '/help/wiki/targets-pt',
            },
            needChangePath: false,
          },
        ],
      },
      {
        title: 'aboutUs',
        links: [
          { id: 1, href: `/about`, text: 'whyAdheart', needChangePath: false },
          {
            id: 2,
            href: `/privacy-policy`,
            text: 'privacyPolicy',
            needChangePath: false,
          },
          {
            id: 3,
            href: '/terms-of-service',
            text: 'termsOfUse',
            needChangePath: false,
          },
          {
            id: 4,
            href: `/cookies-policy`,
            text: 'cookiesPolicy',
            needChangePath: false,
          },
          {
            id: 10,
            href: 'https://docs.google.com/document/d/1gkR_8ldD_QZGKnRmUNa1anh8neNxXfTNuhGH6h1jz1E/edit?tab=t.hmcln973kgf9',
            text: 'refunds',
            needChangePath: false,
            target: '_blank',
          },
          {
            id: 5,
            href: '/partners',
            text: 'partners',
            needChangePath: false,
          },
          { id: 6, href: '/blog/', text: 'blog', needChangePath: true },
        ],
      },
      {
        title: 'contacts',
        links: [
          {
            id: 1,
            href: 'https://t.me/adheart_support_bot',
            text: 'support',
            needChangePath: false,
          },
          {
            id: 2,
            text: 'contactUs',
            needChangePath: false,
            href: `/contact`,
          },
        ],
      },
    ]
  }, [locale, isUserAuth])

  const getTargetLink = (target) => {
    return target ? { target } : {}
  }

  const instagramLink = useMemo(
    () => (LANGUAGE_CODES[locale] ? `.${LANGUAGE_CODES[locale]}` : '_official'),
    [locale]
  )

  const telegramLink =
    locale === 'ru' || locale === 'uk' ? TELEGRAM_LINK : TELEGRAM_LINK_EN

  const socials = useMemo(
    () => [
      {
        href: `https://www.instagram.com/adheart${instagramLink}/`,
        src: '/_next_static/icons/socials/inst.svg',
        alt: 'instagram',
      },
      {
        href: 'https://www.facebook.com/adheartofficial',
        src: '/_next_static/icons/socials/facebook.svg',
        alt: 'facebook',
      },
      {
        href: telegramLink,
        src: '/_next_static/icons/socials/telegram.svg',
        alt: 'telegram',
      },
      {
        href: 'https://www.linkedin.com/company/adheart/',
        src: '/_next_static/icons/socials/linkedin.svg',
        alt: 'linkedin',
      },
    ],
    [locale, telegramLink, instagramLink]
  )

  return (
    <Layout>
      <Container>
        <Block>
          <LogoDataWrap>
            <Logo href="/" isChangePath={false} />
          </LogoDataWrap>
          <LinkWrapperDesktop>
            {FOOTER_LINKS.map((section) => (
              <RulsLinks
                key={section.title}
                onClick={() => {
                  if (section.title === 'payment') {
                    sessionStorage.setItem(PAYMENT_SOURCE, 'footer')
                  }
                }}
              >
                <LinkTitle>{t(section.title)}</LinkTitle>
                {section.links.map((link) => {
                  const href = link.languages
                    ? link.languages[locale]
                    : link.href
                  return href ? (
                    <BaseLinkWrap
                      key={link.id}
                      href={href}
                      passHref
                      isChangePath={link.needChangePath}
                      {...getTargetLink(link.target)}
                    >
                      {t(link.text)}
                    </BaseLinkWrap>
                  ) : null
                })}
              </RulsLinks>
            ))}
          </LinkWrapperDesktop>
          <LinkWrapperMobile>
            <Accordion
              items={FOOTER_LINKS.map((section) => ({
                title: t(section.title),
                links: (
                  <RulsLinks key={section.title}>
                    {section.links.map((link) => {
                      const href = link.languages
                        ? link.languages[locale]
                        : link.href
                      return href ? (
                        <BaseLinkWrap
                          key={link.id}
                          href={href}
                          passHref
                          isChangePath={link.needChangePath}
                          {...getTargetLink(link.target)}
                        >
                          {t(link.text)}
                        </BaseLinkWrap>
                      ) : null
                    })}
                  </RulsLinks>
                ),
              }))}
            />
          </LinkWrapperMobile>
        </Block>
        <SocialsList>
          {socials.map((social, index) => (
            <SocialsItem key={index}>
              <SocialLink href={social.href} target="_blank">
                <SocialImage src={social.src} alt={social.alt} />
              </SocialLink>
            </SocialsItem>
          ))}
        </SocialsList>
        <Divider />
        <BottomBlock>
          <BaseLink href="/terms-of-service" passHref target="_blank">
            <AgreemntText size="12">
              HRET International Development LTD HE 436518 Agias Faneromenis,
              143; PATSIAS COURT, 2nd floor, Flat/Office 201
            </AgreemntText>
          </BaseLink>
          <DateWrapper>
            <TextStyled>{`© 2019-${date} by`}</TextStyled>
            <BaseLink href="/">
              <ByUrl>{BASE_URL}adheart.me</ByUrl>
            </BaseLink>
          </DateWrapper>
        </BottomBlock>
      </Container>
    </Layout>
  )
})

export default Footer
